import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useOrgContext } from '@/contexts/OrgContext'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ThemeToggle'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LogOut, User, Shield, Crown, Building2, X } from 'lucide-react'
import { adminApi } from '@/lib/api'
import { OrgRecord } from '@/types/organization'

export function Header() {
  const { user, logout, isPlatformAdmin } = useAuth()
  const { selectedOrg, setSelectedOrg, orgName } = useOrgContext()
  const [orgs, setOrgs] = useState<OrgRecord[]>([])
  const [orgsLoaded, setOrgsLoaded] = useState(false)

  const loadOrgs = async () => {
    if (orgsLoaded || !isPlatformAdmin) return
    try {
      const data = await adminApi.getOrganizations()
      setOrgs(data.orgs || [])
      setOrgsLoaded(true)
    } catch (err) {
      console.error('Failed to load orgs:', err)
    }
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Admin':
        return <Crown size={14} className="text-primary" />
      case 'Editor':
        return <Shield size={14} className="text-primary" />
      default:
        return <User size={14} className="text-muted-foreground" />
    }
  }

  const getRoleBadge = (role: string) => {
    const baseClasses = "px-2 py-1 text-xs rounded-full font-medium"
    switch (role) {
      case 'Admin':
        return `${baseClasses} bg-primary/10 text-primary`
      case 'Editor':
        return `${baseClasses} bg-primary/10 text-primary`
      default:
        return `${baseClasses} bg-muted text-muted-foreground`
    }
  }

  return (
    <header className="bg-background shadow-sm border-b border-border">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-foreground">
            Admin Dashboard
          </h2>

          {isPlatformAdmin && (
            <div className="flex items-center space-x-2">
              <Building2 size={16} className="text-muted-foreground" />
              <Select
                value={selectedOrg?.slug || '__platform__'}
                onValueChange={value => {
                  if (value === '__platform__') {
                    setSelectedOrg(null)
                  } else {
                    const org = orgs.find(o => o.slug === value)
                    if (org) setSelectedOrg(org)
                  }
                }}
                onOpenChange={open => {
                  if (open) loadOrgs()
                }}
              >
                <SelectTrigger className="w-[200px] h-8">
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__platform__">Platform (Global)</SelectItem>
                  {orgs.map(org => (
                    <SelectItem key={org.slug} value={org.slug}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedOrg && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setSelectedOrg(null)}
                  title="Clear org selection"
                >
                  <X size={14} />
                </Button>
              )}
            </div>
          )}

          {/* Dedicated tenant: the org is fixed, so show which one rather than
              a switcher. Without this a tenant admin got no indication at all
              of whose data they were looking at. */}
          {!isPlatformAdmin && orgName && (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Building2 size={16} />
              <span className="font-medium text-foreground">{orgName}</span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <User size={16} />
              <span>{user?.email}</span>
            </div>

            {user?.role && (
              <div className="flex items-center space-x-1">
                {getRoleIcon(user.role)}
                <span className={getRoleBadge(user.role)}>
                  {user.role}
                </span>
              </div>
            )}
          </div>

          <ThemeToggle />

          <Button
            onClick={logout}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </Button>
        </div>
      </div>
    </header>
  )
}
